// =============================================================================
// Copyright   : 2026 semantics GmbH
// Purpose     : controlled image loading reducers
// =============================================================================
// ---Framework imports
import { dict, mapItems, values, items } from 'ldash';
import { handleAction } from 'uix/actions';
// ---Local imports
import { requestViewerImage, releaseViewerImage, setViewerImageState, removeViewerImage } from './actions';
let getViewerImagePriority = (consumers)=>{
    let priorities = values(consumers || {}).map((consumer)=>consumer.priority || 0);
    return priorities.length ? Math.max(...priorities) : 0;
};
export const imageRequestsHandlers = [
    handleAction(requestViewerImage, (data, { payload })=>{
        let { src, consumerId, viewerId, priority } = payload;
        let prevEntry = data.imageRequests[src] || {
            state: 'queued',
            consumers: {}
        };
        let prevConsumer = prevEntry.consumers[consumerId];
        let nextState = prevEntry.state == 'loaded' ? 'loaded' : prevEntry.state == 'broken' ? 'queued' : prevEntry.state;
        if (prevConsumer?.viewerId == viewerId && prevConsumer?.priority == priority && prevEntry.state == nextState) {
            return data;
        }
        let consumers = {
            ...prevEntry.consumers,
            [consumerId]: {
                viewerId,
                priority
            }
        };
        return {
            ...data,
            imageRequests: {
                ...data.imageRequests,
                [src]: {
                    ...prevEntry,
                    state: nextState,
                    consumers,
                    priority: getViewerImagePriority(consumers),
                    error: null
                }
            }
        };
    }),
    handleAction(releaseViewerImage, (data, { payload })=>{
        let { src, consumerId } = payload;
        let prevEntry = data.imageRequests[src];
        if (!prevEntry) {
            return data;
        }
        if (!prevEntry.consumers[consumerId]) {
            return data;
        }
        let consumers = dict(mapItems(prevEntry.consumers, (id, consumer)=>[
                id,
                consumer
            ], (id)=>id !== consumerId));
        return {
            ...data,
            imageRequests: {
                ...data.imageRequests,
                [src]: {
                    ...prevEntry,
                    consumers,
                    priority: getViewerImagePriority(consumers)
                }
            }
        };
    }),
    handleAction(setViewerImageState, (data, { payload })=>{
        let { src, state, data: extra } = payload;
        let prevEntry = data.imageRequests[src];
        if (!prevEntry) {
            return data;
        }
        let isSameState = prevEntry.state == state && items(extra || {}).every(([key, value])=>prevEntry[key] == value);
        if (isSameState) {
            return data;
        }
        return {
            ...data,
            imageRequests: {
                ...data.imageRequests,
                [src]: {
                    ...prevEntry,
                    state,
                    ...extra
                }
            }
        };
    }),
    handleAction(removeViewerImage, (data, { payload })=>{
        let prevEntry = data.imageRequests[payload.src];
        if (!prevEntry) {
            return data;
        }
        return {
            ...data,
            imageRequests: dict(mapItems(data.imageRequests, (src, entry)=>[
                    src,
                    entry
                ], (src)=>src !== payload.src))
        };
    })
];
export const imageRequestsInitialState = {
    imageRequests: {}
};
